import styled from "@emotion/styled";

import {
  CommercialMeterKey,
  CommercialMeterKind,
  CommercialUsageStatus,
  type CommercialPlanSnapshot,
  type CommercialUsageMeterSnapshot,
} from "@/modules/commercial-plans";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSProgressBar,
  MDSProgressBarColor,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

const COMMERCIAL_METER_PRESENTATION: Record<
  CommercialMeterKey,
  { label: string; description: string }
> = {
  [CommercialMeterKey.UserRequests]: {
    label: "Agent requests",
    description: "Requests you send to Mem Agent",
  },
  [CommercialMeterKey.NewNotes]: {
    label: "New notes",
    description: "Notes created this billing period",
  },
  [CommercialMeterKey.ActiveNotes]: {
    label: "Active notes",
    description: "Notes you created or edited for active processing",
  },
  [CommercialMeterKey.OpenTasksProjects]: {
    label: "Open tasks + active projects",
    description: "Working memory monitored at the same time",
  },
  [CommercialMeterKey.ActiveCustomRoutines]: {
    label: "Active custom routines",
    description: "Enabled routines at the same time",
  },
  [CommercialMeterKey.RoutineExecutions]: {
    label: "Routine executions",
    description: "Custom routine runs this billing period",
  },
  [CommercialMeterKey.ActiveConnections]: {
    label: "Active connections",
    description: "Enabled external connections at the same time",
  },
  [CommercialMeterKey.VoiceModeHours]: {
    label: "Voice Mode hours",
    description: "Finalized recording time this billing period",
  },
};

const formatQuantity = ({ key, value }: { key: CommercialMeterKey; value: number }): string => {
  if (key === CommercialMeterKey.VoiceModeHours) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)}h`;
  }

  return new Intl.NumberFormat().format(value);
};

const getDisplayUsage = (meter: CommercialUsageMeterSnapshot): number => {
  if (
    meter.kind === CommercialMeterKind.Gauge &&
    meter.current !== null &&
    meter.current !== undefined
  ) {
    return meter.current;
  }

  return meter.used;
};

const getUsageColor = (meter: CommercialUsageMeterSnapshot): MDSProgressBarColor => {
  if (meter.status === CommercialUsageStatus.AtLimit) {
    return MDSProgressBarColor.Danger;
  }

  if (meter.status === CommercialUsageStatus.Nearing) {
    return MDSProgressBarColor.Brand;
  }

  return MDSProgressBarColor.Emphasis;
};

const getStatusLabel = (meter: CommercialUsageMeterSnapshot): string | null => {
  if (meter.allowance === null) {
    return null;
  }

  if (meter.allowance === 0) {
    return "Not included";
  }

  if (meter.status === CommercialUsageStatus.AtLimit) {
    return "At limit";
  }

  if (meter.status === CommercialUsageStatus.Nearing) {
    const displayUsage = getDisplayUsage(meter);
    const usageRatio = meter.allowance === 0 ? 1 : displayUsage / meter.allowance;
    return usageRatio >= 0.9 ? "Almost at limit" : "75% used";
  }

  return null;
};

const CommercialUsageRow = ({ meter }: { meter: CommercialUsageMeterSnapshot }) => {
  const presentation = COMMERCIAL_METER_PRESENTATION[meter.key];
  const displayUsage = getDisplayUsage(meter);
  const formattedUsage = formatQuantity({ key: meter.key, value: displayUsage });
  const formattedAllowance =
    meter.allowance === null
      ? "Unlimited"
      : formatQuantity({ key: meter.key, value: meter.allowance });
  const statusLabel = getStatusLabel(meter);

  return (
    <UsageRow>
      <UsageLabel>
        <UsageLabelLine>
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
            {presentation.label}
          </MDSText>
          {statusLabel ? (
            <MDSBadge
              label={statusLabel}
              variant={
                meter.allowance === 0
                  ? MDSBadgeVariant.Default
                  : meter.status === CommercialUsageStatus.Nearing
                    ? MDSBadgeVariant.Emphasis
                    : MDSBadgeVariant.Negative
              }
            />
          ) : null}
        </UsageLabelLine>
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          {presentation.description}
        </MDSText>
      </UsageLabel>

      <UsageBar>
        {meter.allowance === null ? null : (
          <MDSProgressBar
            value={Math.min(displayUsage, meter.allowance)}
            minValue={0}
            maxValue={Math.max(1, meter.allowance)}
            aria-label={presentation.label}
            aria-valuetext={`${formattedUsage} of ${formattedAllowance}`}
            size="xlarge"
            color={getUsageColor(meter)}
            animation={{ duration: 0 }}
            rounded
          />
        )}
      </UsageBar>

      <UsageValue>
        <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
          {formattedUsage} / {formattedAllowance}
        </MDSText>
        {meter.kind === CommercialMeterKind.Gauge &&
        meter.peak !== null &&
        meter.peak !== undefined ? (
          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Peak {formatQuantity({ key: meter.key, value: meter.peak })}
          </MDSText>
        ) : null}
      </UsageValue>
    </UsageRow>
  );
};

export const CommercialUsageProgress = ({ snapshot }: { snapshot: CommercialPlanSnapshot }) => (
  <UsageList aria-label="Plan usage">
    {Object.values(CommercialMeterKey).map(key => (
      <CommercialUsageRow key={key} meter={snapshot.meters[key]} />
    ))}
  </UsageList>
);

const UsageList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const UsageRow = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, 1fr) minmax(140px, 1.25fr) minmax(100px, auto);
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: 64px;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  }

  @container mds-panel (max-width: 620px) {
    grid-template-columns: 1fr minmax(90px, auto);
  }
`;

const UsageLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const UsageLabelLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const UsageBar = styled.div`
  min-width: 0;

  @container mds-panel (max-width: 620px) {
    grid-column: 1 / -1;
    grid-row: 2;
  }
`;

const UsageValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
`;
